import { api } from '../console/api.ts';
import { carrierCell, carrierName } from '../console/carriers.ts';
import { empty, field, idRef, pageHead, pill, skel } from '../console/components.ts';
import { ApiError, InputError } from '../console/errors.ts';
import {
  agentName,
  cap,
  date,
  esc,
  fmt,
  guatemalaMonth,
  icon,
  lineName,
  type Money,
  period,
  person,
  when,
} from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { has } from '../console/session.ts';
import { $, act, toast } from '../console/ui.ts';
import { monthControl, selectControl, textControl } from '../controls.ts';
import type { Option } from './cotizar.ts';
import { focusPolicy } from './polizas.ts';

/**
 * «Renovaciones» (#308, ADR-0034): the policies that come to the end of their period and how each
 * renewal is going — who works it, the premium now and the carrier's offer, its status, the
 * notices sent — with the renewal rate of the month and the year. From a row: consult the offer
 * and renew, send the proposal (only where the policyholder consented), record a contact, hand it
 * to another agent, requote with other carriers or mark it as not renewed.
 */

type Notice = { kind: string; window: string; sentAt: string };
export type RenewalRow = {
  policyId: string;
  scheduleId: string;
  policyNumber: string;
  carrier: string;
  carrierName: string;
  productLine: string;
  plan: { code: string; name: string | null } | null;
  policyholder: { fullName: string } | null;
  agentId: string;
  responsible: string;
  periodEnd: string | null;
  daysLeft: number | null;
  status: string;
  currentPremium: Money | null;
  offeredPremium: Money | null;
  offer: { consultedAt: string; byWorkOrder: boolean; option: string | null } | null;
  variationPercent: number | null;
  lost: { reason: string; note: string | null; at: string } | null;
  proposal: { channel: string; sentAt: string } | null;
  quoteId: string | null;
  renewedById: string | null;
  contacts: number;
  lastContactAt: string | null;
  notices: Notice[];
};
type RenewalDetail = RenewalRow & {
  contactHistory: { at: string; by: string; channel: string; note: string }[];
  proposalChannels: string[];
};
type Indicators = {
  due: number;
  renewed: number;
  lost: number;
  pending: number;
  rate: number | null;
  premium: { currency: string; retained: Money; lost: Money }[];
  lostByReason: Record<string, number>;
};
type Offer = {
  keepsPolicyNumber: boolean;
  startsOn: string;
  quote: { decision: string; validUntil: string; options: Option[] };
};

/** The status of a renewal as the agency says it, and its tone. */
export const RENEWAL_STATUS: Record<string, [label: string, tone: string]> = {
  PENDING: ['Pendiente', 'DRAFT'],
  CONTACTED: ['En gestión', 'info'],
  PROPOSAL_SENT: ['Propuesta enviada', 'info'],
  REQUOTED: ['Recotizada', 'warn'],
  RENEWED: ['Renovada', 'ok'],
  NOT_RENEWED: ['No renovada', 'bad'],
};
export const LOST_REASON: Record<string, string> = {
  PRICE: 'Precio',
  OTHER_CARRIER: 'Se fue a otra aseguradora',
  SOLD_ASSET: 'Vendió el bien',
  NON_PAYMENT: 'Falta de pago',
  OTHER: 'Otro motivo',
  NO_FOLLOW_UP: 'Sin gestión',
};
const CONTACT: Record<string, string> = {
  CALL: 'Llamada',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'Correo',
  MEETING: 'Reunión',
  NOTE: 'Nota',
};
const CHANNEL: Record<string, string> = { WHATSAPP: 'WhatsApp', EMAIL: 'correo' };

const statusPill = (s: string) => {
  const [label, tone] = RENEWAL_STATUS[s] ?? [s, 'info'];
  return pill(tone, label);
};
const pct = (r: number | null) => (r === null ? '—' : `${Math.round(r * 100)} %`);
/** +5 % · −3.3 % · 0 %: tabular, signed, with a decimal comma only when it has one. */
export const variationText = (v: number | null) =>
  v === null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${String(Math.abs(v))} %`;
const daysText = (n: number | null) =>
  n === null
    ? 'sin fin de vigencia'
    : n < 0
      ? `venció hace ${-n} ${-n === 1 ? 'día' : 'días'}`
      : n === 0
        ? 'vence hoy'
        : n === 1
          ? 'vence mañana'
          : `en ${n} días`;
const daysTone = (n: number | null) =>
  n === null ? 'info' : n < 0 ? 'bad' : n <= 7 ? 'bad' : n <= 30 ? 'warn' : 'info';
const noticeChip = (n: Notice) => {
  const days = n.window.replace(/^D-/, '');
  const who = n.kind === 'RENEWAL_POLICYHOLDER' ? 'Contratante' : 'Aviso';
  return `<span class="chip" title="Enviado el ${esc(when(n.sentAt))}">${icon('bell')}${esc(who)} ${esc(days)} d</span>`;
};

/** Who works what (the API and Cerbos decide; this only shapes the screen). */
const manages = () => has('agency_director', 'agency_admin', 'agent');
const assigns = () => has('agency_director', 'agency_admin', 'agency_manager');

/** The tab on screen: 30, 60 or 90 days ahead, the expired ones, or one month. */
type Tab = '30' | '60' | '90' | 'expired' | 'month';
let tab: Tab = '30';
let month = guatemalaMonth(new Date());
/** The row whose follow-up is open (it stays open across a repaint). */
let openId: string | null = null;
/** A renewal to bring into view after the next paint (a notice's «renovaciones?renovar=<id>»). */
let focusId: string | null = null;

/** A new session starts on the first tab, with nothing open. */
export function resetRenewals() {
  tab = '30';
  month = guatemalaMonth(new Date());
  openId = null;
  focusId = null;
}

const TABS: [Tab, string][] = [
  ['30', '30 días'],
  ['60', '60 días'],
  ['90', '90 días'],
  ['expired', 'Vencidas sin renovar'],
];

const query = () =>
  tab === 'month' ? `month=${month}` : tab === 'expired' ? 'view=expired' : `days=${tab}`;

function kpis(m: Indicators, y: Indicators, mo: string, rows: RenewalRow[]) {
  const gtq = (i: Indicators) => i.premium.find((p) => p.currency === 'GTQ');
  const retained = gtq(m)?.retained ?? null;
  const lost = gtq(m)?.lost ?? null;
  const usd = m.premium.find((p) => p.currency === 'USD');
  const unworked = rows.filter((r) => r.status === 'PENDING').length;
  const tile = (iconName: string, label: string, value: string, foot: string, tone = '') =>
    `<div class="card kpi ${tone}"><span class="kpi-label">${icon(iconName)}${esc(label)}</span><span class="kpi-value">${value}</span><span class="kpi-foot">${foot}</span></div>`;
  return `<section class="kpis" aria-label="Indicadores de renovación" data-component="renewal-kpis">
    ${tile('trend', 'Tasa de renovación del mes', pct(m.rate), m.due ? `${m.renewed} renovadas de ${m.due} · ${esc(period(mo))}` : `Nada decidido aún en ${esc(period(mo))}`)}
    ${tile('chart', `Tasa del año ${mo.slice(0, 4)}`, pct(y.rate), y.due ? `${y.renewed} renovadas de ${y.due}` : 'Nada decidido aún este año')}
    ${tile('ok', 'Prima retenida del mes', fmt(retained ?? { amountMinor: 0, currency: 'GTQ' }), `perdida ${fmt(lost ?? { amountMinor: 0, currency: 'GTQ' })}${usd ? ` · en dólares ${fmt(usd.retained)} / ${fmt(usd.lost)}` : ''}`)}
    ${tile('warn', 'Sin gestión', String(unworked), unworked ? 'pólizas de esta lista sin contacto aún' : 'Todas tienen gestión', unworked ? 'warn' : '')}
  </section>`;
}

function lostReasons(m: Indicators) {
  const entries = Object.entries(m.lostByReason).filter(([, n]) => n > 0);
  if (!entries.length) return '';
  return `<p class="renewal-reasons muted small" data-component="lost-reasons"><strong>No renovadas del mes por motivo:</strong> ${entries
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `<span class="chip">${esc(LOST_REASON[k] ?? k)} · ${n}</span>`)
    .join(' ')}</p>`;
}

function toolbar() {
  const tabs = TABS.map(
    ([k, label]) =>
      `<button type="button" class="btn sm" role="tab" id="rt-${k}" aria-selected="${tab === k}" aria-pressed="${tab === k}" aria-controls="renewals-panel" data-ren-tab="${k}">${esc(label)}</button>`,
  ).join('');
  const now = guatemalaMonth(new Date());
  const [y, m] = now.split('-').map(Number) as [number, number];
  // A year ahead (the periods of the policies in force) and a year back (the ones that ended).
  const ahead = y * 12 + (m - 1) + 12;
  const last = `${Math.floor(ahead / 12)}-${String((ahead % 12) + 1).padStart(2, '0')}`;
  return `<div class="card toolbar renewal-toolbar">
    <div class="lead-filters" role="tablist" aria-label="Qué pólizas ver">${tabs}</div>
    <span class="spacer"></span>
    ${field('Por mes de vencimiento', monthControl({ id: 'ren-month', value: tab === 'month' ? month : '', today: last, months: 25, placeholder: 'Elige un mes' }))}
  </div>`;
}

/** A cell with its column's name, which the phone shows in front of it (a stacked row). */
const td = (label: string, html: string, cls = '') =>
  `<td data-label="${esc(label)}"${cls ? ` class="${cls}"` : ''}>${html}</td>`;

function row(r: RenewalRow) {
  const id = esc(r.policyId);
  const plan = [lineName(r.productLine), r.plan?.name ?? r.plan?.code].filter(Boolean).join(' · ');
  const offered = r.offeredPremium
    ? `<span class="premium">${fmt(r.offeredPremium)}</span>${r.offer ? `<div class="muted small">consultada el ${esc(date(r.offer.consultedAt))}</div>` : ''}`
    : r.offer?.byWorkOrder
      ? '<span class="muted small">La aseguradora la cotiza por orden de trabajo</span>'
      : '<span class="muted small">Sin consultar</span>';
  const variation =
    r.variationPercent === null
      ? '<span class="muted">—</span>'
      : `<span class="variation ${r.variationPercent > 0 ? 'up' : r.variationPercent < 0 ? 'down' : ''}">${esc(variationText(r.variationPercent))}</span>`;
  const outcome =
    r.status === 'NOT_RENEWED' && r.lost
      ? `<div class="muted small">${esc(LOST_REASON[r.lost.reason] ?? r.lost.reason)}</div>`
      : r.status === 'RENEWED'
        ? '<div class="muted small">con la nueva vigencia</div>'
        : r.lastContactAt
          ? `<div class="muted small">último contacto ${esc(when(r.lastContactAt))}</div>`
          : '';
  const open = openId === r.policyId;
  return `<tr data-renewal="${id}" class="${open ? 'open' : ''}">
    ${td('Póliza', `<span class="mono">${esc(r.policyNumber)}</span><div class="muted small">${esc(plan)}</div>`, 'r-policy')}
    ${td('Cliente', r.policyholder ? esc(r.policyholder.fullName) : '<span class="muted">Sin contratante registrado</span>', 'r-client')}
    ${td('Aseguradora', carrierCell(r.carrier, r.carrierName || carrierName(r.carrier)))}
    ${td('Agente', `${esc(agentName(r.agentId))}${r.responsible !== r.agentId ? `<div class="muted small">la gestiona ${esc(agentName(r.responsible))}</div>` : ''}`)}
    ${td('Vence', `<span class="nowrap">${esc(date(r.periodEnd))}</span><div>${pill(daysTone(r.daysLeft), daysText(r.daysLeft))}</div>`)}
    ${td('Prima actual', `<span class="premium">${fmt(r.currentPremium)}</span>`, 'num')}
    ${td('Renovación', offered, 'num')}
    ${td('Variación', variation, 'num')}
    ${td('Estado', `${statusPill(r.status)}${outcome}`, 'r-status')}
    ${td('Avisos', `<div class="chips">${r.notices.length ? r.notices.map(noticeChip).join('') : '<span class="muted small">Sin avisos</span>'}</div>`)}
    <td class="actions"><button type="button" class="btn sm${open ? ' primary' : ''}" data-ren-open="${id}" aria-expanded="${open}" aria-controls="ren-panel-${id}" aria-label="Gestionar la renovación de ${esc(r.policyNumber)}">${open ? 'Cerrar' : 'Gestionar'}</button></td>
  </tr>
  <tr class="renewal-detail-row" id="ren-panel-${id}" ${open ? '' : 'hidden'}><td colspan="11"><div class="renewal-detail" data-ren-panel="${id}">${open ? `<div class="toolbar">${skel(14, '40%')}</div>` : ''}</div></td></tr>`;
}

export async function renovaciones() {
  const [list, ind] = await Promise.all([
    api<{ window: { from: string; to: string; today: string }; items: RenewalRow[] }>(
      'GET',
      `/v1/renewal-cases?${query()}`,
    ),
    api<{ month: string; ofMonth: Indicators; ofYear: Indicators }>(
      'GET',
      `/v1/renewal-indicators${tab === 'month' ? `?month=${month}` : ''}`,
    ),
  ]);
  const rows = list.items;
  const head = pageHead(
    'Renovaciones',
    has('agent') && !has('agency_director', 'agency_admin')
      ? 'Tus pólizas que vencen y cómo va cada renovación: consulta la oferta, envía la propuesta y renueva.'
      : 'Las pólizas que vencen, quién gestiona cada renovación y cómo va: prima actual contra la de renovación, avisos y motivo cuando se pierde.',
  );
  const what =
    tab === 'month'
      ? `que vencen en ${period(month).toLowerCase()}`
      : tab === 'expired'
        ? 'que vencieron sin renovar en los últimos 90 días'
        : `que vencen en los próximos ${tab} días`;
  const table = rows.length
    ? `<div class="table-wrap" data-component="table"><table class="table renewals-table"><caption class="sr-only">Pólizas ${esc(what)}</caption>
      <thead><tr><th>Póliza</th><th>Cliente</th><th>Aseguradora</th><th>Agente</th><th>Vence</th><th class="num">Prima actual</th><th class="num">Renovación</th><th class="num">Variación</th><th>Estado</th><th>Avisos</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead>
      <tbody>${rows.map(row).join('')}</tbody></table></div>`
    : empty(
        'ok',
        tab === 'expired' ? 'Ninguna venció sin renovar' : 'Ninguna póliza en esta lista',
        tab === 'expired'
          ? 'En los últimos 90 días no venció ninguna póliza sin su renovación.'
          : `No hay pólizas ${esc(what)}. Prueba con un plazo más largo o con otro mes.`,
      );
  // Once the router paints this screen (a timer: after its own await).
  setTimeout(() => void reopen(), 0);
  return `${head}${kpis(ind.ofMonth, ind.ofYear, ind.month, rows)}${lostReasons(ind.ofMonth)}${toolbar()}
    <section class="card renewals" id="renewals-panel" role="tabpanel" aria-labelledby="${tab === 'month' ? 'ren-month' : `rt-${tab}`}" data-component="renewals">
      <div class="card-head"><h2>Pólizas ${esc(what)}</h2><span class="spacer"></span><span class="muted small">${rows.length === 1 ? '1 póliza' : `${rows.length} pólizas`}</span></div>
      ${table}</section>`;
}

/** After a paint: the open row loads its follow-up; a notice's renewal comes into view. */
async function reopen() {
  if (focusId) {
    const id = focusId;
    focusId = null;
    const tr = document.querySelector<HTMLElement>(`[data-renewal="${CSS.escape(id)}"]`);
    if (!tr) {
      toast('Esa póliza ya no está entre las que vencen en este plazo.', 'error');
      return;
    }
    openId = id;
    await render('renovaciones', { quiet: true });
    document
      .querySelector<HTMLElement>(`[data-renewal="${CSS.escape(id)}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  if (openId) {
    const id = openId;
    await loadDetail(id);
    await fillAgents(id);
  }
}

async function loadDetail(id: string) {
  const panel = document.querySelector<HTMLElement>(`[data-ren-panel="${CSS.escape(id)}"]`);
  if (!panel) return;
  try {
    const d = await api<RenewalDetail>('GET', `/v1/renewal-cases/${encodeURIComponent(id)}`);
    panel.innerHTML = detail(d);
  } catch {
    panel.innerHTML = `<p class="note">${icon('alert')}No se pudo cargar la gestión de esta renovación. <button type="button" class="btn sm" data-ren-open="${esc(id)}" data-reload="1">Reintentar</button></p>`;
  }
}

function detail(d: RenewalDetail) {
  const id = esc(d.policyId);
  const closed = d.status === 'RENEWED' || d.status === 'NOT_RENEWED';
  const timeline = d.contactHistory.length
    ? `<ol class="timeline">${[...d.contactHistory]
        .reverse()
        .map(
          (c) =>
            `<li>${pill('info', CONTACT[c.channel] ?? c.channel)}<span class="muted small">${esc(when(c.at))} · ${esc(person(c.by.replace(/^member:/, 'human:').replace(/^agent:/, '')))}</span><span class="timeline-note">${esc(c.note)}</span></li>`,
        )
        .join('')}</ol>`
    : '<p class="muted small">Sin contactos todavía.</p>';
  const facts: string[] = [];
  if (d.lost)
    facts.push(
      `<p class="renewal-fact">${icon('alert')}<span>No renovada: <strong>${esc(LOST_REASON[d.lost.reason] ?? d.lost.reason)}</strong>${d.lost.note ? ` · ${esc(d.lost.note)}` : ''} <span class="muted small">(${esc(when(d.lost.at))})</span></span></p>`,
    );
  if (d.renewedById)
    facts.push(
      `<p class="renewal-fact">${icon('ok')}<span>Renovada: la nueva vigencia enlaza con esta póliza.</span><button type="button" class="btn sm" data-ren-policy="${esc(d.renewedById)}">${icon('policy')}Ver la nueva póliza</button></p>`,
    );
  if (d.proposal)
    facts.push(
      `<p class="renewal-fact">${icon('bell')}<span>Propuesta enviada por ${esc(CHANNEL[d.proposal.channel] ?? d.proposal.channel)} el ${esc(when(d.proposal.sentAt))}.</span></p>`,
    );
  if (d.quoteId)
    facts.push(
      `<p class="renewal-fact">${icon('quote')}<span>Recotizada en la cotización ${idRef(d.quoteId)}.</span><button type="button" class="btn sm" data-open-quote="${esc(d.quoteId)}">Ver cotización</button></p>`,
    );
  const actions: string[] = [];
  if (!closed && manages())
    actions.push(`<div class="renewal-action">
      <h4>Oferta de la aseguradora</h4>
      <p class="muted small">${d.offer ? `Última consulta: ${esc(when(d.offer.consultedAt))}.` : 'Pide a la aseguradora el precio de la nueva vigencia.'}</p>
      <div class="toolbar"><button type="button" class="btn primary" data-ren-offer="${id}">${icon('trend')}Consultar oferta y renovar</button></div>
      <div class="renewal-offer" data-ren-offer-panel="${id}" hidden></div></div>`);
  if (!closed && manages()) {
    const channels = d.proposalChannels;
    actions.push(`<div class="renewal-action">
      <h4>Propuesta al contratante</h4>
      ${
        channels.length
          ? `<p class="muted small">Le llega con la prima de la última oferta${d.offeredPremium ? ` (${fmt(d.offeredPremium)})` : ''}.</p>
        <div class="toolbar">${channels
          .map(
            (ch) =>
              `<button type="button" class="btn" data-ren-proposal="${id}" data-channel="${esc(ch)}"${d.offeredPremium ? '' : ' disabled'}>${icon('bell')}Enviar por ${esc(CHANNEL[ch] ?? ch)}</button>`,
          )
          .join(
            '',
          )}</div>${d.offeredPremium ? '' : '<p class="note small">Consulta la oferta primero: la propuesta dice su prima.</p>'}`
          : `<p class="note small">${icon('lock')}El contratante no ha aceptado mensajes por WhatsApp ni por correo en su ficha. Si lo contactas de otra forma, regístralo abajo.</p>`
      }</div>`);
  }
  if (!closed && has('agency_director', 'agency_admin', 'agency_manager', 'agent'))
    actions.push(`<div class="renewal-action">
      <h4>Registrar contacto</h4>
      <div class="toolbar">
        ${field(
          'Medio',
          selectControl(
            { id: `rc-ch-${id}` },
            Object.entries(CONTACT).map(([value, label]) => ({ value, label })),
          ),
        )}
        <div class="grow">${field('Qué se habló', textControl({ id: `rc-note-${id}`, maxlength: 500, placeholder: 'Le interesa renovar; pide la oferta' }))}</div>
        <button type="button" class="btn" data-ren-contact="${id}">Registrar</button>
      </div></div>`);
  if (!closed && assigns())
    actions.push(`<div class="renewal-action">
      <h4>Responsable</h4>
      <div class="toolbar">
        <div class="grow" data-ren-agents="${id}">${field('Agente que la gestiona', selectControl({ id: `ra-${id}` }, [{ value: d.responsible, label: agentName(d.responsible), selected: true }]))}</div>
        <button type="button" class="btn" data-ren-assign="${id}">Asignar</button>
      </div></div>`);
  if (!closed && manages())
    actions.push(`<div class="renewal-action">
      <h4>Otras aseguradoras</h4>
      <p class="muted small">Cotiza la nueva vigencia con todas las aseguradoras de la agencia; la cotización queda enlazada.</p>
      <div class="toolbar"><button type="button" class="btn" data-ren-requote="${id}">${icon('quote')}Recotizar</button></div></div>`);
  if (!closed && manages())
    actions.push(`<div class="renewal-action renewal-lost">
      <h4>No se renueva</h4>
      <div class="toolbar">
        ${field(
          'Motivo',
          selectControl(
            { id: `rl-reason-${id}` },
            Object.entries(LOST_REASON)
              .filter(([k]) => k !== 'NO_FOLLOW_UP')
              .map(([value, label]) => ({ value, label })),
          ),
        )}
        <div class="grow">${field('Nota (obligatoria con «Otro motivo»)', textControl({ id: `rl-note-${id}`, maxlength: 500 }))}</div>
        <button type="button" class="btn danger" data-ren-lost="${id}">Marcar no renovada</button>
      </div></div>`);
  return `<div class="renewal-detail-grid">
    <div class="renewal-history"><h3>Gestión</h3>${facts.join('')}${timeline}</div>
    <div class="renewal-actions">${actions.length ? actions.join('') : `<p class="muted small">${icon('lock')}${closed ? 'Esta renovación ya está cerrada.' : 'Solo lees esta renovación.'}</p>`}</div>
  </div>`;
}

const value = (id: string) =>
  (document.getElementById(id) as HTMLInputElement | null)?.value?.trim() ?? '';

/** Runs a command of a renewal and repaints the screen with the row open. */
function command(t: HTMLElement, busy: string, run: () => Promise<string>) {
  void act(t, busy, async () => {
    const said = await run();
    await render(current, { quiet: true });
    return said;
  });
}

/** Opens Renovaciones on a notice's renewal (`renovaciones?renovar=<id>`). */
export async function focusRenewal(id: string) {
  focusId = id;
  openId = null;
  // The notices go out 60, 30 and 7 days before the end: all of them fall in 90 days.
  tab = '90';
  await render('renovaciones');
}

export const actions: Actions = {
  click: {
    renTab: (_, d) => {
      const next = d.renTab as Tab | undefined;
      if (!next) return;
      tab = next;
      openId = null;
      void render('renovaciones', { quiet: true }).then(() => $(`#rt-${next}`)?.focus());
    },
    renOpen: (t, d) => {
      const id = d.renOpen;
      if (!id) return;
      if (d.reload) {
        void loadDetail(id);
        return;
      }
      openId = openId === id ? null : id;
      const panel = document.getElementById(`ren-panel-${id}`);
      if (!panel) return;
      // Only one follow-up open at a time.
      for (const other of document.querySelectorAll<HTMLElement>('.renewal-detail-row'))
        if (other !== panel) other.hidden = true;
      for (const b of document.querySelectorAll<HTMLButtonElement>('[data-ren-open]')) {
        const mine = b.dataset.renOpen === openId;
        b.setAttribute('aria-expanded', String(mine));
        b.classList.toggle('primary', mine);
        b.textContent = mine ? 'Cerrar' : 'Gestionar';
      }
      panel.hidden = openId !== id;
      t.closest('tr')?.classList.toggle('open', openId === id);
      if (openId === id) {
        const box = panel.querySelector<HTMLElement>('[data-ren-panel]');
        if (box) box.innerHTML = `<div class="toolbar">${skel(14, '40%')}</div>`;
        void loadDetail(id).then(() => fillAgents(id));
      }
    },
    renOffer: (t, d) => {
      const id = d.renOffer;
      if (!id) return;
      const panel = document.querySelector<HTMLElement>(
        `[data-ren-offer-panel="${CSS.escape(id)}"]`,
      );
      void act(t, 'Pidiendo la oferta de renovación a la aseguradora…', async () => {
        const r = await api<{ renewal: RenewalRow; offer: Offer | null }>(
          'POST',
          `/v1/renewal-cases/${encodeURIComponent(id)}/offer`,
          {},
        );
        if (!panel) return 'Oferta consultada.';
        panel.hidden = false;
        if (!r.offer) {
          panel.innerHTML = `<p class="note">La aseguradora renueva esta póliza por orden de trabajo: pídela desde «Pólizas y recibos» › Renovar.</p>`;
          return 'Esta renovación va por orden de trabajo.';
        }
        const priced = r.offer.quote.options.filter((x) => x.premium);
        panel.innerHTML = `<p class="muted small">Nueva vigencia desde el ${esc(date(r.offer.startsOn))} · ${r.offer.keepsPolicyNumber ? 'conserva el número de póliza' : 'la aseguradora emite un número nuevo'} · válida hasta el ${esc(date(r.offer.quote.validUntil))}</p>
        ${
          priced.length
            ? `<div class="table-wrap" data-component="table"><table class="table quote"><caption class="sr-only">Opciones de renovación</caption><thead><tr><th>Plan</th><th class="num">Prima anual</th><th class="num">Cuotas</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>${priced
                .map(
                  (x) =>
                    `<tr><td><span class="option-name">${esc(x.carrierOptionCode)}</span></td><td class="num"><span class="premium">${fmt(x.premium?.gross)}</span></td><td class="num">${x.premium?.installments.count} × ${fmt(x.premium?.installments.regular)}</td>
                  <td class="actions"><button type="button" class="btn sm primary" data-ren-renew="${esc(r.renewal.scheduleId)}" data-option="${esc(x.carrierOptionCode)}" aria-label="Renovar con ${esc(x.carrierOptionCode)}">Renovar</button></td></tr>`,
                )
                .join('')}</tbody></table></div>`
            : '<p class="note">La aseguradora no ofrece opciones con precio para renovar esta póliza.</p>'
        }`;
        // The row shows the premium offered now (repainted without closing the offer).
        const tr = document.querySelector<HTMLElement>(`[data-renewal="${CSS.escape(id)}"]`);
        const cells = tr?.querySelectorAll('td');
        if (cells?.[6] && r.renewal.offeredPremium)
          cells[6].innerHTML = `<span class="premium">${fmt(r.renewal.offeredPremium)}</span><div class="muted small">consultada ahora</div>`;
        if (cells?.[7])
          cells[7].innerHTML =
            r.renewal.variationPercent === null
              ? '<span class="muted">—</span>'
              : `<span class="variation ${r.renewal.variationPercent > 0 ? 'up' : r.renewal.variationPercent < 0 ? 'down' : ''}">${esc(variationText(r.renewal.variationPercent))}</span>`;
        return `Oferta recibida: ${r.renewal.offeredPremium ? fmt(r.renewal.offeredPremium) : 'sin precio'} (${variationText(r.renewal.variationPercent)}).`;
      });
    },
    renRenew: (t, d) => {
      const schedule = d.renRenew;
      if (!schedule) return;
      command(t, 'Renovando con la aseguradora…', async () => {
        const r = await api<{
          policyNumber: string;
          period: { start: string };
          keepsPolicyNumber: boolean;
        }>('POST', `/v1/schedules/${encodeURIComponent(schedule)}/renew`, { option: d.option });
        return `Póliza ${r.policyNumber} renovada desde el ${date(r.period.start)}${r.keepsPolicyNumber ? '' : ' (número nuevo)'}.`;
      });
    },
    renProposal: (t, d) => {
      const id = d.renProposal;
      if (!id) return;
      command(t, 'Enviando la propuesta al contratante…', async () => {
        try {
          await api('POST', `/v1/renewal-cases/${encodeURIComponent(id)}/proposal`, {
            channel: d.channel,
          });
        } catch (e) {
          if (e instanceof ApiError && e.code === 'NO_CONSENT')
            throw new InputError(
              'El contratante no aceptó mensajes por ese medio en su ficha. Registra el contacto que hagas por otro medio.',
            );
          throw e;
        }
        return `Propuesta enviada por ${CHANNEL[d.channel ?? ''] ?? d.channel}.`;
      });
    },
    renContact: (t, d) => {
      const id = d.renContact;
      if (!id) return;
      const note = value(`rc-note-${id}`);
      if (note.length < 3) {
        toast('Escribe qué se habló con el contratante.', 'error');
        $(`#rc-note-${CSS.escape(id)}`)?.focus();
        return;
      }
      command(t, 'Registrando el contacto…', async () => {
        await api('POST', `/v1/renewal-cases/${encodeURIComponent(id)}/contacts`, {
          channel: value(`rc-ch-${id}`) || 'CALL',
          note,
        });
        return 'Contacto registrado.';
      });
    },
    renAssign: (t, d) => {
      const id = d.renAssign;
      if (!id) return;
      const agentId = value(`ra-${id}`);
      command(t, 'Asignando la renovación…', async () => {
        await api('POST', `/v1/renewal-cases/${encodeURIComponent(id)}/assign`, { agentId });
        return `Ahora la gestiona ${agentName(agentId)}.`;
      });
    },
    renRequote: (t, d) => {
      const id = d.renRequote;
      if (!id) return;
      command(t, 'Cotizando la nueva vigencia con las aseguradoras…', async () => {
        const r = await api<{ quote: { id: string }; quoted: number }>(
          'POST',
          `/v1/renewal-cases/${encodeURIComponent(id)}/requote`,
          {},
        );
        return `Recotizada: ${r.quoted === 1 ? '1 aseguradora cotizó' : `${r.quoted} aseguradoras cotizaron`}. Ábrela con «Ver cotización».`;
      });
    },
    renLost: (t, d) => {
      const id = d.renLost;
      if (!id) return;
      const reason = value(`rl-reason-${id}`);
      const note = value(`rl-note-${id}`);
      if (reason === 'OTHER' && !note) {
        toast('Escribe por qué no se renueva.', 'error');
        $(`#rl-note-${CSS.escape(id)}`)?.focus();
        return;
      }
      command(t, 'Marcando la renovación como no renovada…', async () => {
        await api('POST', `/v1/renewal-cases/${encodeURIComponent(id)}/not-renewed`, {
          reason,
          note: note || null,
        });
        return `Renovación marcada como no renovada: ${LOST_REASON[reason] ?? reason}.`;
      });
    },
    renPolicy: (_, d) => {
      if (!d.renPolicy) return;
      const next = d.renPolicy;
      void render('polizas').then(() => focusPolicy(next));
    },
  },
  change: {
    'ren-month': (control) => {
      if (!control.value) return;
      month = control.value;
      tab = 'month';
      openId = null;
      void render('renovaciones', { quiet: true }).then(() => $('#ren-month')?.focus());
    },
  },
};

/** The agents of the agency in the «Responsable» select (read once the follow-up is open). */
async function fillAgents(id: string) {
  const box = document.querySelector<HTMLElement>(`[data-ren-agents="${CSS.escape(id)}"]`);
  if (!box) return;
  const chosen = value(`ra-${id}`);
  const agents = await api<{ agentId: string }[]>('GET', '/v1/agents').catch(() => null);
  if (!agents?.length) return;
  box.innerHTML = field(
    'Agente que la gestiona',
    selectControl(
      { id: `ra-${id}` },
      agents.map((a) => ({
        value: a.agentId,
        label: cap(a.agentId),
        selected: a.agentId === chosen,
      })),
    ),
  );
}
